/**
 * What a COG actually *is*, read from TiTiler `/cog/info`.
 *
 * Band selection used to be guessed from the layer name ("ortho", "rgb", …).
 * A converter-produced RGB COG named after its source file therefore rendered
 * as band 1 with a gray colormap — a colour image shown in grayscale, while
 * QGIS (which reads the file) shows it in colour. Name hints stay as the
 * pre-network default; this module is the authority once the header is read.
 *
 * `/cog/info` is a header read and is cached per COG, so this costs at most one
 * small request per source for the whole session.
 */

import { ensureTitilerPngTileTemplate } from '@/utils/titilerSourceHost';
import { requestTileSize } from '@/utils/rasterTileResolution';

export type TitilerRasterProfile = {
  readonly bandCount: number;
  /** GDAL dtype of the first band, e.g. `uint8`, `float32`. */
  readonly dtype: string;
  /** Per-band colour interpretation; often `undefined` on converter output. */
  readonly colorInterpretations: readonly string[];
  readonly nodataValue: number | null;
  /** Raster width in pixels (from `/cog/info`). */
  readonly width: number | null;
  /** Raster height in pixels (from `/cog/info`). */
  readonly height: number | null;
  /**
   * Native CRS as TiTiler reports it (often an OGC URI like
   * `http://www.opengis.net/def/crs/EPSG/0/32640`).
   */
  readonly crs: string | null;
  /**
   * Native CRS bounds in the CRS's own units
   * `[minX, minY, maxX, maxY]` — degrees for geographic, metres for projected.
   */
  readonly bounds: readonly [number, number, number, number] | null;
  /** GDAL driver short name, e.g. `GTiff`. */
  readonly driver: string | null;
  /** Overview levels reported by the COG (empty when none). */
  readonly overviews: readonly number[];
};

const profileCache = new Map<string, TitilerRasterProfile | null>();
const inFlight = new Map<string, Promise<TitilerRasterProfile | null>>();

/** Split a TiTiler XYZ template into its service base and encoded `url=` value. */
export function extractCogQuery(tilesUrl: string): { base: string; cogParam: string } | null {
  const baseMatch = tilesUrl.match(/^(.*?)\/cog\/tiles\//);
  const urlParamMatch = tilesUrl.match(/[?&]url=([^&]+)/);
  if (!baseMatch || !urlParamMatch) return null;
  return { base: baseMatch[1], cogParam: urlParamMatch[1] };
}

/**
 * Query-string helpers that work on XYZ *templates*. `new URL()` percent-encodes
 * the `{z}/{x}/{y}` placeholders, so templates are edited textually.
 */
export function upsertQueryParam(url: string, key: string, value: string): string {
  const re = new RegExp(`([?&])${key}=[^&]*`);
  if (re.test(url)) return url.replace(re, `$1${key}=${encodeURIComponent(value)}`);
  const sep = url.includes('?') ? '&' : '?';
  return `${url}${sep}${key}=${encodeURIComponent(value)}`;
}

export function removeQueryParam(url: string, key: string): string {
  const start = url.indexOf('?');
  if (start < 0) return url;
  // Split rather than regex-replace: repeated keys (`bidx=1&bidx=2&bidx=3`)
  // share separators, so a global replace skips every second occurrence.
  const kept = url
    .slice(start + 1)
    .split('&')
    .filter((param) => param !== key && !param.startsWith(`${key}=`));
  const base = url.slice(0, start);
  return kept.length > 0 ? `${base}?${kept.join('&')}` : base;
}

/** Replace every `bidx` in a template with the given band indices, in order. */
export function setBandIndices(url: string, bands: readonly number[]): string {
  const stripped = removeQueryParam(url, 'bidx');
  const sep = stripped.includes('?') ? '&' : '?';
  return `${stripped}${sep}${bands.map((band) => `bidx=${band}`).join('&')}`;
}

/** How many bands a template asks TiTiler for. */
export function requestedBandCount(url: string): number {
  return (url.match(/[?&]bidx=/g) ?? []).length;
}

function normalizeDtype(value: unknown): string {
  if (typeof value === 'string') return value.toLowerCase();
  if (Array.isArray(value) && typeof value[0] === 'string') return value[0].toLowerCase();
  return '';
}

function readFiniteNumber(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function readBounds(value: unknown): readonly [number, number, number, number] | null {
  if (!Array.isArray(value) || value.length < 4) return null;
  const nums = value.slice(0, 4).map((entry) => Number(entry));
  if (nums.some((n) => !Number.isFinite(n))) return null;
  return [nums[0], nums[1], nums[2], nums[3]];
}

function readOverviews(value: unknown): readonly number[] {
  if (!Array.isArray(value)) return [];
  return value.map((entry) => Number(entry)).filter((n) => Number.isFinite(n) && n > 0);
}

/**
 * Parse a TiTiler / rio-tiler `/cog/info` JSON body into the profile shape.
 * Exported for unit tests — production code goes through
 * {@link fetchTitilerRasterProfile}.
 */
export function parseTitilerRasterProfile(json: unknown): TitilerRasterProfile {
  const body = (json && typeof json === 'object' ? json : {}) as Record<string, unknown>;
  return {
    bandCount: typeof body.count === 'number' && body.count > 0 ? body.count : 0,
    dtype: normalizeDtype(body.dtype),
    colorInterpretations: (Array.isArray(body.colorinterp) ? body.colorinterp : [])
      .map((entry) => String(entry).toLowerCase()),
    nodataValue: readFiniteNumber(body.nodata_value),
    width: readFiniteNumber(body.width),
    height: readFiniteNumber(body.height),
    crs: typeof body.crs === 'string' && body.crs.trim().length > 0 ? body.crs.trim() : null,
    bounds: readBounds(body.bounds),
    driver: typeof body.driver === 'string' && body.driver.trim().length > 0
      ? body.driver.trim()
      : null,
    overviews: readOverviews(body.overviews),
  };
}

export async function fetchTitilerRasterProfile(
  tilesUrl: string,
): Promise<TitilerRasterProfile | null> {
  const parts = extractCogQuery(tilesUrl);
  if (!parts) return null;
  const key = parts.cogParam;
  if (profileCache.has(key)) return profileCache.get(key) ?? null;
  const pending = inFlight.get(key);
  if (pending) return pending;

  const request = (async (): Promise<TitilerRasterProfile | null> => {
    try {
      const res = await fetch(`${parts.base}/cog/info?url=${key}`, {
        signal: AbortSignal.timeout(12000),
      });
      if (!res.ok) {
        profileCache.set(key, null);
        return null;
      }
      const profile = parseTitilerRasterProfile(await res.json());
      profileCache.set(key, profile);
      return profile;
    } catch {
      // Do not cache transport failures — a later mount should retry.
      return null;
    } finally {
      inFlight.delete(key);
    }
  })();

  inFlight.set(key, request);
  return request;
}

function isEightBit(dtype: string): boolean {
  return dtype === 'uint8' || dtype === 'int8';
}

/**
 * True for imagery that must be composited from three bands.
 *
 * Restricted to 8-bit data on purpose: those samples already fill the display
 * range, so `bidx=1,2,3` alone reproduces what QGIS shows. Deeper multiband
 * data (uint16 satellite scenes) needs a per-band stretch and is left to the
 * single-band path rather than rendered near-black.
 */
export function isMultibandColorProfile(profile: TitilerRasterProfile | null): boolean {
  if (!profile || profile.bandCount < 3) return false;
  const interp = profile.colorInterpretations;
  const taggedRgb =
    interp.includes('red') && interp.includes('green') && interp.includes('blue');
  // Converter output frequently leaves colour interpretation undefined, so band
  // count plus depth has to be enough on its own.
  return taggedRgb || isEightBit(profile.dtype);
}

/**
 * Render a template as true colour: three bands, no colormap, no stretch.
 * Any single-band colourisation baked in earlier is removed.
 */
export function applyRgbBandParams(tilesUrl: string): string {
  let out = ensureTitilerPngTileTemplate(tilesUrl);
  out = removeQueryParam(out, 'colormap_name');
  out = removeQueryParam(out, 'rescale');
  out = setBandIndices(out, [1, 2, 3]);
  out = upsertQueryParam(out, 'tilesize', String(requestTileSize()));
  out = upsertQueryParam(out, 'resampling', 'bilinear');
  return out;
}

/**
 * Clamp a template to band 1. Guards the inverse mistake: a name that reads as
 * imagery on a single-band file makes TiTiler reject `bidx=2` and the map stays
 * empty.
 */
export function applySingleBandSelection(tilesUrl: string): string {
  if (requestedBandCount(tilesUrl) <= 1) return tilesUrl;
  return setBandIndices(tilesUrl, [1]);
}
